import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient, { API_BASE_URL } from '../utils/apiClient';
import authClient from '../utils/authClient';
import { fetchIpLocations, mergeIpLocations } from '../utils/ipLocations';
import './ManageFeedbackThread.css';

const FEEDBACK_TYPES = {
  game_issue: '游戏问题',
  info_error: '信息错误',
  manual_request: '手册请求',
  new_game: '新游戏请求',
  recondb_entry: 'ReconDB 提交',
  site_bug: '网站 Bug',
  system_notice: '系统公告'
};

const STATUS_TYPES = {
  open: '待处理',
  replied: '已回复',
  delayed: '进行中',
  completed: '已完成',
  rejected: '暂不处理'
};

const formatIpLocation = (location, ipAddress) => {
  const formattedLocation = [location?.country, location?.regionName, location?.city, location?.district]
    .filter(Boolean)
    .filter((part, index, parts) => parts.indexOf(part) === index)
    .join(' · ');
  return formattedLocation || ipAddress || '未知位置';
};

const ManageFeedbackThread = () => {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const currentUser = authClient.getCurrentUser();
  const canAdminister = authClient.isAdmin();
  const [thread, setThread] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [newComment, setNewComment] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    if (!authClient.isAuthenticated()) {
      navigate('/login', { state: { from: { pathname: `/manage-feedback-thread/${threadId}` } } });
    }
  }, [navigate, threadId]);

  const fetchThread = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await apiClient.get(`/feedback/threads/${threadId}`);
      setThread(response.data.thread);
      const nextComments = Array.isArray(response.data.comments) ? response.data.comments : [];
      setComments(nextComments);
      fetchIpLocations([response.data.thread, ...nextComments])
        .then((locations) => {
          setThread((current) => mergeIpLocations([current], locations)[0]);
          setComments((current) => mergeIpLocations(current, locations));
        })
        .catch((locationError) => console.error('Error loading IP locations:', locationError));
    } catch (error) {
      console.error('Error fetching feedback thread:', error);
      setLoadError('无法加载这条反馈。');
    } finally {
      setLoading(false);
    }
  }, [threadId]);

  useEffect(() => {
    if (threadId) fetchThread();
  }, [fetchThread, threadId]);

  const parseAttachments = (value) => {
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [value];
    } catch {
      return [value];
    }
  };

  const isImage = (path) => /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(path);

  const renderAttachments = (value) => {
    const files = parseAttachments(value);
    if (!files.length) return null;
    return (
      <div className="manage-thread-attachments">
        {files.map((path, index) => (
          isImage(path) ? (
            <a key={`${path}-${index}`} href={`${API_BASE_URL}${path}`} target="_blank" rel="noreferrer">
              <img src={`${API_BASE_URL}${path}`} alt={`附件 ${index + 1}`} />
            </a>
          ) : (
            <a className="manage-thread-file" key={`${path}-${index}`} href={`${API_BASE_URL}${path}`} target="_blank" rel="noreferrer">
              <span aria-hidden="true">↗</span>
              {path.split('/').pop()}
            </a>
          )
        ))}
      </div>
    );
  };

  const formatDate = (value) => new Date(value).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });

  const handleStatusUpdate = async (status) => {
    const previousStatus = thread.status;
    setThread((current) => ({ ...current, status }));
    try {
      await apiClient.put(`/feedback/threads/${threadId}/status`, { status });
    } catch (error) {
      console.error('Error updating feedback status:', error);
      setThread((current) => ({ ...current, status: previousStatus }));
      alert('状态更新失败');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!newComment.trim()) return;
    setSubmitting(true);
    const formData = new FormData();
    formData.append('content', newComment.trim());
    formData.append('is_developer', '1');
    attachments.forEach((file, index) => formData.append(`attachment_${index}`, file));
    try {
      await apiClient.post(`/feedback/threads/${threadId}/comments`, formData);
      setNewComment('');
      setAttachments([]);
      await fetchThread();
    } catch (error) {
      console.error('Error submitting developer response:', error);
      alert('回复提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  const beginEdit = (comment) => {
    setEditingId(comment.id);
    setEditingContent(comment.content);
  };

  const handleEdit = async (commentId) => {
    const content = editingContent.trim();
    if (!content) return;
    setSavingEdit(true);
    try {
      const response = await apiClient.put(`/feedback/comments/${commentId}`, { content });
      setComments((current) => current.map((comment) => (
        comment.id === commentId
          ? { ...comment, content, edited_at: response.data.edited_at || new Date().toISOString() }
          : comment
      )));
      setEditingId(null);
      setEditingContent('');
    } catch (error) {
      console.error('Error editing response:', error);
      alert('回复修改失败');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('确定删除这条回复吗？')) return;
    try {
      await apiClient.delete(`/feedback/comments/${commentId}`);
      setComments((current) => current.filter((comment) => comment.id !== commentId));
    } catch (error) {
      console.error('Error deleting response:', error);
      alert('回复删除失败');
    }
  };

  if (loading) {
    return <main className="manage-feedback-thread-page"><div className="manage-thread-state">正在加载反馈…</div></main>;
  }

  if (loadError || !thread) {
    return (
      <main className="manage-feedback-thread-page">
        <div className="manage-thread-state">
          <p>{loadError || '反馈不存在。'}</p>
          <button onClick={() => navigate('/manage')}>返回反馈管理</button>
        </div>
      </main>
    );
  }

  return (
    <main className="manage-feedback-thread-page">
      <div className="manage-thread-shell">
        <header className="manage-thread-header">
          <button className="manage-thread-back" onClick={() => navigate('/manage')}>← 返回反馈管理</button>
          <strong className="manage-thread-current-user">{currentUser?.displayName || '馆长'}</strong>
        </header>

        <section className="manage-thread-summary">
          <div className="manage-thread-summary__top">
            <div className="manage-thread-labels">
              <span className="manage-thread-type">{FEEDBACK_TYPES[thread.type] || thread.type}</span>
              <span>反馈 #{thread.id}</span>
            </div>
            {canAdminister ? <label className="manage-thread-status">
              <select value={thread.status} onChange={(event) => handleStatusUpdate(event.target.value)}>
                {Object.entries(STATUS_TYPES).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label> : (
              <span className={`manage-thread-status-label status-${thread.status}`}>
                {STATUS_TYPES[thread.status] || thread.status}
              </span>
            )}
          </div>
          <h1>{thread.title}</h1>
          {thread.game_name && <p className="manage-thread-game">关联游戏 · {thread.game_name}</p>}
          <div className="manage-thread-content">{thread.content}</div>
          {renderAttachments(thread.attachment)}
          <footer>
            <span>{formatDate(thread.created_at)}</span>
            <span>{thread.upvotes || 0} 赞</span>
            <span>{thread.downvotes || 0} 踩</span>
            <span>{comments.length} 条回复</span>
            {thread.ip_address && (
              <span title={thread.ip_address}>IP 位置：{formatIpLocation(thread.location, thread.ip_address)}</span>
            )}
          </footer>
        </section>

        <div className="manage-thread-columns">
          <section className="manage-thread-replies">
            <header className="manage-thread-section-title">
              <h2>全部回复 <span>{comments.length}</span></h2>
            </header>
            {comments.length === 0 ? (
              <div className="manage-thread-no-replies">还没有回复。</div>
            ) : (
              <div className="manage-thread-reply-list">
                {comments.map((comment) => {
                  const developer = comment.is_developer === 1;
                  const responder = comment.responder_display_name || comment.responder_name || '馆长';
                  return (
                    <article key={comment.id} className={`manage-thread-reply ${developer ? 'is-developer' : ''}`}>
                      <header>
                        <div className="manage-thread-author">
                          <div>
                            <strong>{developer ? responder : '访客回复'}</strong>
                            <span>{formatDate(comment.created_at)}</span>
                          </div>
                        </div>
                        <div className="manage-thread-reply-actions">
                          <button onClick={() => beginEdit(comment)}>编辑</button>
                          <button className="is-danger" onClick={() => handleDelete(comment.id)}>删除</button>
                        </div>
                      </header>
                      {editingId === comment.id ? (
                        <div className="manage-thread-edit">
                          <textarea
                            value={editingContent}
                            onChange={(event) => setEditingContent(event.target.value)}
                            rows="4"
                            autoFocus
                          />
                          <div>
                            <button onClick={() => { setEditingId(null); setEditingContent(''); }}>取消</button>
                            <button
                              className="is-primary"
                              disabled={savingEdit || !editingContent.trim()}
                              onClick={() => handleEdit(comment.id)}
                            >
                              {savingEdit ? '保存中…' : '保存修改'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="manage-thread-reply-content">{comment.content}</div>
                      )}
                      {renderAttachments(comment.attachment)}
                      <footer>
                        {comment.edited_at && <span>已编辑 {formatDate(comment.edited_at)}</span>}
                        <span>{comment.upvotes || 0} 赞 · {comment.downvotes || 0} 踩</span>
                        {comment.ip_address && (
                          <span title={comment.ip_address}>IP 位置：{formatIpLocation(comment.location, comment.ip_address)}</span>
                        )}
                      </footer>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          <aside className="manage-thread-composer">
            <div className="manage-thread-section-title">
              <h2>添加回复</h2>
            </div>
            <div className="manage-thread-identity">
              回复人：<strong>{currentUser?.displayName || '馆长'}</strong>
            </div>
            <form onSubmit={handleSubmit}>
              <textarea
                value={newComment}
                onChange={(event) => setNewComment(event.target.value)}
                placeholder="输入回复…"
                rows="7"
                required
              />
              <label className="manage-thread-upload">
                <span>添加附件</span>
                <small>最多 3 个文件，每个最大 50 MB</small>
                <input
                  type="file"
                  multiple
                  onChange={(event) => {
                    const selected = Array.from(event.target.files || []);
                    setAttachments((current) => [...current, ...selected].slice(0, 3));
                    event.target.value = '';
                  }}
                  disabled={attachments.length >= 3}
                />
              </label>
              {attachments.length > 0 && (
                <div className="manage-thread-pending-files">
                  {attachments.map((file, index) => (
                    <div key={`${file.name}-${index}`}>
                      <span>{file.name}</span>
                      <button type="button" onClick={() => setAttachments((current) => current.filter((_, itemIndex) => itemIndex !== index))}>×</button>
                    </div>
                  ))}
                </div>
              )}
              <button className="manage-thread-submit" disabled={submitting || !newComment.trim()}>
                {submitting ? '正在发布…' : '发布回复'}
              </button>
            </form>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default ManageFeedbackThread;
